import React from "react";
import { TextField, IconButton } from "@mui/material";
import { AddCircle, Delete } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";

const BulkOfferItemsTable = ({ products, offerItems, setOfferItems, darkMode }) => {
  const handleChange = (index, field, value) => {
    const updatedItems = [...offerItems];
    const item = updatedItems[index];

    // --- Anyagváltás --------------------------------------------------------
    if (field === "productId") {
      const prod = products.find((p) => p.id === value);
      item.productId = value;
      item.materialName = prod?.name || "";

      // reset mezők (biztonságból mindent kinullázunk)
      item.length = "";
      item.width = "";
      item.quantity = "";
      item.quantityMP = "";
      item.quantityML = "";
      item.quantityBUC = "";
      item.isManualMP = false;
    }

    // --- Numerikus mezők ----------------------------------------------------
    else if (["quantityMP", "quantityML", "quantityBUC"].includes(field)) {
      item[field] = value === "" ? "" : parseFloat(value);

      // manuális MP -> méretmezők törlése
      if (field === "quantityMP") {
        item.isManualMP = true;
        item.length = "";
        item.width = "";
        item.quantity = "";
      }
    }

    // --- Egyéb mezők (length, width, quantity, customDescription, stb.) -----
    else {
      item[field] = value;

      // ha méret/darab mező változik -> újra MP számítás (ha minden megvan)
      if (["length", "width", "quantity"].includes(field)) {
        item.isManualMP = false;

        if (item.length && item.width && item.quantity) {
          const mp =
            (parseFloat(item.length) *
              parseFloat(item.width) *
              parseInt(item.quantity, 10)) /
            10000;
          item.quantityMP = parseFloat(mp.toFixed(4));
        } else {
          item.quantityMP = 0;
        }
      }
    }

    setOfferItems(updatedItems);
  };

  const handleAddRow = () => {
    const last = offerItems[offerItems.length - 1] || {};
    setOfferItems([
      ...offerItems,
      {
        productId: last.productId || "",
        materialName: last.materialName || "",
        customDescription: "",
        length: "",
        width: "",
        quantity: "",
        quantityMP: "",
        quantityML: "",
        quantityBUC: "",
        isManualMP: false,
      },
    ]);
  };

  const handleDeleteRow = (index) => {
    const updated = [...offerItems];
    updated.splice(index, 1);
    setOfferItems(updated);
  };

  const totalMP = offerItems
    .reduce((acc, item) => acc + (parseFloat(item.quantityMP) || 0), 0)
    .toFixed(4);
  const totalML = offerItems
    .reduce((acc, item) => acc + (parseFloat(item.quantityML) || 0), 0)
    .toFixed(4);
  const totalBUC = offerItems
    .reduce((acc, item) => acc + (parseFloat(item.quantityBUC) || 0), 0)
    .toFixed(0);

  return (
    <div className="overflow-x-auto">
      <table
        className={`w-full border shadow-lg text-sm ${
          darkMode
            ? "border-gray-700 bg-gray-900 text-gray-300"
            : "border-gray-300 bg-white text-gray-900"
        }`}
      >
        <thead>
          <tr
            className={`${
              darkMode ? "bg-gray-700 text-white" : "bg-gray-200 text-gray-900"
            }`}
          >
            <th className="border p-2">Material</th>
            <th className="border p-2">Descriere</th>
            <th className="border p-2">Lungime</th>
            <th className="border p-2">Latime</th>
            <th className="border p-2">Bucăți</th>
            <th className="border p-2">MP</th>
            <th className="border p-2">ML</th>
            <th className="border p-2">BUC</th>
            <th className="border p-2">Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {offerItems.map((item, index) => {
            const product = products.find((p) => p.id === item.productId);
            const unitType = product?.unitType; // "MP" | "ML" | "BUC" (vagy bármi)

            const isBUC = unitType === "BUC";
            const isMeasured = unitType === "MP" || unitType === "ML";

            return (
              <tr key={index}>
                {/* Material */}
                <td className="border p-1 min-w-[300px] max-w-[400px]">
                  <Autocomplete
                    options={products}
                    getOptionLabel={(option) => option.name}
                    value={product || null}
                    onChange={(event, newValue) => {
                      handleChange(index, "productId", newValue?.id || "");
                      handleChange(index, "materialName", newValue?.name || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Selectează materialul"
                        variant="standard"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        inputProps={{
                          ...params.inputProps,
                          style: { textAlign: "center" },
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": {
                        paddingLeft: "6px",
                        textAlign: "center",
                      },
                    }}
                  />
                </td>

                {/* Descriere */}
                <td className="border p-1 min-w-[220px] max-w-[360px]">
                  <TextField
                    variant="standard"
                    size="small"
                    placeholder="Descriere personalizată"
                    value={item.customDescription || ""}
                    onChange={(e) =>
                      handleChange(index, "customDescription", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    fullWidth
                  />
                </td>

                {/* Lungime */}
                <td className="border p-1">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={item.length}
                    onChange={(e) =>
                      handleChange(index, "length", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    disabled={isBUC}
                  />
                </td>

                {/* Latime */}
                <td className="border p-1">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={item.width}
                    onChange={(e) =>
                      handleChange(index, "width", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    disabled={isBUC}
                  />
                </td>

                {/* Bucăți (darabszám lapokra, nem BUC termék!) */}
                <td className="border p-1">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={item.quantity}
                    onChange={(e) =>
                      handleChange(index, "quantity", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    disabled={isBUC}
                  />
                </td>

                {/* MP */}
                <td className="border p-1">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={item.quantityMP}
                    onChange={(e) =>
                      handleChange(index, "quantityMP", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    disabled={isBUC}
                  />
                </td>

                {/* ML */}
                <td className="border p-1">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={item.quantityML}
                    onChange={(e) =>
                      handleChange(index, "quantityML", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    disabled={isBUC}
                  />
                </td>

                {/* BUC (csak BUC típusnál aktív) */}
                <td className="border p-1">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={item.quantityBUC}
                    onChange={(e) =>
                      handleChange(index, "quantityBUC", e.target.value)
                    }
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    disabled={isMeasured}
                  />
                </td>

                {/* Actions */}
                <td className="border p-1 text-center">
                  <IconButton onClick={() => handleDeleteRow(index)}>
                    <Delete color="error" />
                  </IconButton>
                </td>
              </tr>
            );
          })}
          {/* Totals */}
          <tr>
            <td colSpan={4} className="text-right font-bold p-2">
              MP Total:
            </td>
            <td className="text-center font-bold">{totalMP}</td>
            <td className="text-right font-bold p-2">ML Total:</td>
            <td className="text-center font-bold">{totalML}</td>
            <td className="text-center font-bold">{totalBUC}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={handleAddRow}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold transition ${
            darkMode
              ? "bg-blue-700 hover:bg-blue-600 text-white"
              : "bg-blue-600 hover:bg-blue-500 text-white"
          }`}
        >
          <AddCircle />
          Adaugă rând nou
        </button>
      </div>
    </div>
  );
};

export default BulkOfferItemsTable;
